import { FC } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { GarantieReferentielDto } from 'api/guarantee';
import { StripedList } from 'back-office/shared/components/BlockCard';
import { RowActions } from 'back-office/shared/components/RowActions';
import { DamageBadge, DerivedPremiumRoleBadge } from './guaranteeLabels';
import { Branch } from './useBranches';

interface GuaranteeRowsProps {
  guarantees: GarantieReferentielDto[];
  onEdit: (guarantee: GarantieReferentielDto) => void;
  onDelete: (guarantee: GarantieReferentielDto) => void;
  /** Affiche la branche de rattachement — utile hors du contexte d'une branche. */
  showBranch?: boolean;
  /**
   * Branche dont les garanties sont listées. Le rôle dans les primes dérivées n'est
   * affiché que si elle les calcule : ailleurs, il est ignoré.
   */
  branch?: Branch;
}

/**
 * Lignes de garanties du référentiel, partagées par l'écran d'une branche et par celui
 * des garanties sans branche : mêmes colonnes, mêmes actions, un seul endroit à corriger.
 */
export const GuaranteeRows: FC<GuaranteeRowsProps> = ({
  guarantees,
  onEdit,
  onDelete,
  showBranch,
  branch,
}) => (
  <StripedList>
    {guarantees.map((guarantee) => (
      <li key={guarantee.id} className="flex items-center gap-3 px-5 py-2.5">
        <span className="w-20 flex-shrink-0 truncate font-mono text-[11px] text-gray-400">
          {guarantee.code || '—'}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-zinc-800">
              {guarantee.shortLabel || guarantee.label || '—'}
            </span>
            {branch?.derivedPremiumsEnabled && (
              <DerivedPremiumRoleBadge role={guarantee.derivedPremiumRole} rates={branch} />
            )}
            <DamageBadge value={guarantee.damage} />
          </div>
          {guarantee.label && guarantee.label !== guarantee.shortLabel && (
            <p className="truncate text-xs text-gray-400">{guarantee.label}</p>
          )}
        </div>

        {showBranch && (
          <span className="hidden flex-shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500 lg:inline">
            {guarantee.branchCode || 'sans branche'}
          </span>
        )}

        <RowActions
          actions={[
            {
              tone: 'edit',
              label: 'Modifier la garantie',
              icon: <Pencil className="h-4 w-4" />,
              onClick: () => onEdit(guarantee),
            },
            {
              tone: 'danger',
              label: 'Supprimer la garantie',
              icon: <Trash2 className="h-4 w-4" />,
              onClick: () => onDelete(guarantee),
            },
          ]}
        />
      </li>
    ))}
  </StripedList>
);
